// Copyright (c) 2025-2026, IPivot. All rights reserved.
'use strict';

import { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Button, Table, Spinner, Modal } from 'react-bootstrap';
import { StatusContext } from './StatusContext';
import { mergeDeleteQueueItem, mergeImportQueue, mergeMoveQueueItem, mergeFetchRaw, mergeLoadFile, MergeQueueEntry } from '../data';
import { useNavigate, useSearchParams } from 'react-router-dom';

type QueueDir = 'queue' | 'originals' | 'processed';

// Extract YYMMDDHHMM sort key from filenames like "neon_041626_1546.merge"
function mergeFileSortKey(name: string): string {
    const m = name.match(/_(\d{2})(\d{2})(\d{2})_(\d{4})(?:\.\w+)?$/);
    if (!m) return name;
    const [, mm, dd, yy, hhmm] = m;
    return `${yy}${mm}${dd}${hhmm}`;
}

function MergeQueuePage() {
    const [searchParams] = useSearchParams();
    const [ cloudQueue, setCloudQueue ] = useState<boolean>(true);
    const [ queueDir, setQueueDir ] = useState<QueueDir>(
        (searchParams.get('dir') as QueueDir) || 'queue'
    );
    const [ queue, setQueue ] = useState<MergeQueueEntry[]>([]);
    const [ queueLoading, setQueueLoading ] = useState<boolean>(false);
    const [ queueRefresh, setQueueRefresh ] = useState<number>(0);
    const [ rawItem, setRawItem ] = useState<{ name: string; content: string } | null>(null);
    const statusContext = useContext(StatusContext);
    const navigate = useNavigate();

    useEffect(() => {
        async function getQueue() {
            try {
                setQueueLoading(true);
                const rc = await mergeImportQueue(cloudQueue, queueDir);
                setQueue((rc.queue || []).slice().sort((a, b) => mergeFileSortKey(a.name).localeCompare(mergeFileSortKey(b.name))));
            } catch (error: any) {
                statusContext.postError(`Error loading merge queue: ${error.message}`);
            } finally {
                setQueueLoading(false);
            }
        }
        getQueue();
    }, [cloudQueue, queueDir, queueRefresh]);

    async function mergeQueueItem(item: MergeQueueEntry) {
        try {
            // For non-queue dirs, use loadName (full path/S3 key) so the server can resolve the right directory.
            const nameArg = queueDir === 'queue' ? item.name : (item.loadName || item.name);
            await mergeLoadFile(cloudQueue, nameArg, queueDir === 'queue');
            navigate('/data/merge-op');
        } catch (e: any) {
            statusContext.postError(`Failed to load merge: ${e.message}`);
        }
    }

    async function showRaw(item: MergeQueueEntry) {
        try {
            // For non-queue dirs, use loadName (full path/S3 key) so the server can resolve the right directory.
            const nameArg = queueDir === 'queue' ? item.name : (item.loadName || item.name);
            const res = await mergeFetchRaw(cloudQueue, nameArg, queueDir === 'queue');
            setRawItem({ name: item.name, content: res.raw });
        } catch (e: any) {
            statusContext.postError(`Failed to fetch raw file: ${e.message}`);
        }
    }

    async function requeueItem(item: MergeQueueEntry) {
        try {
            await mergeMoveQueueItem(cloudQueue, item.name, queueDir, 'queue');
            statusContext.postInfo(`Requeued ${item.name}`);
            setQueueRefresh(v => v + 1);
        } catch (e: any) {
            statusContext.postError(`Failed to requeue ${item.name}: ${e.message}`);
        }
    }

    async function deleteQueueItem(item: MergeQueueEntry) {
        try {
            await mergeDeleteQueueItem(cloudQueue, item.name, queueDir);
            statusContext.postInfo(`Deleted ${item.name}`);
            setQueueRefresh(v => v + 1);
        } catch (e: any) {
            statusContext.postError(`Failed to delete ${item.name}: ${e.message}`);
        }
    }

    const isArchiveDir = queueDir === 'originals' || queueDir === 'processed';

    return (
        <>
        <Container fluid>
            <Row className="px-4">
                <Col className="col-10">
                    <h3 className="mt-4">Merge Queue</h3>
                    <Row className="mb-3">
                        <Col>
                            <div className="btn-group" role="group">
                                <Button variant={cloudQueue ? 'primary' : 'outline-primary'} onClick={() => setCloudQueue(true)}>Cloud</Button>
                                <Button variant={!cloudQueue ? 'primary' : 'outline-primary'} onClick={() => setCloudQueue(false)}>Local</Button>
                            </div>
                            <div className="btn-group ms-3" role="group">
                                <Button variant={queueDir === 'queue' ? 'primary' : 'outline-primary'} onClick={() => setQueueDir('queue')}>Queue</Button>
                                <Button variant={queueDir === 'originals' ? 'primary' : 'outline-primary'} onClick={() => setQueueDir('originals')}>Originals</Button>
                                <Button variant={queueDir === 'processed' ? 'primary' : 'outline-primary'} onClick={() => setQueueDir('processed')}>Processed</Button>
                            </div>
                        </Col>
                        <Col className="text-end">
                            <Button variant="outline-primary" className="me-2" onClick={() => setQueueRefresh(v => v + 1)}>Refresh</Button>
                        </Col>
                    </Row>
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th style={{ minWidth: '12rem' }}>Name</th>
                                <th style={{ minWidth: '16rem' }}>Notes</th>
                                <th>FP</th>
                                <th>Match</th>
                                <th>FP Cat</th>
                                <th>Cat</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {queueLoading && (
                                <tr>
                                    <td colSpan={7} className="text-center">
                                        <Spinner animation="border" role="status" size="sm" className="me-2" />
                                        Loading queue...
                                    </td>
                                </tr>
                            )}
                            {!queueLoading && queue.length === 0 && (
                                <tr>
                                    <td colSpan={7}>No queue entries found.</td>
                                </tr>
                            )}
                            {queue.map((item) => (
                                <tr key={item.name}>
                                    <td style={{ wordBreak: 'break-all' }}>{item.name}</td>
                                    <td style={{ wordBreak: 'break-word' }}>{item.metadata?.info || ''}</td>
                                    <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                        <span className="text-success">+{item.addedFingerprints}</span>
                                        {' / '}
                                        <span className="text-danger">−{item.removedFingerprints}</span>
                                    </td>
                                    <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                        <span className="text-success">+{item.addedMatches}</span>
                                        {' / '}
                                        <span className="text-danger">−{item.removedMatches}</span>
                                    </td>
                                    <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                        <span className="text-success">+{item.addedFpCategories}</span>
                                        {' / '}
                                        <span className="text-danger">−{item.removedFpCategories}</span>
                                    </td>
                                    <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                        <span className="text-success">+{item.addedCategories}</span>
                                        {' / '}
                                        <span className="text-danger">−{item.removedCategories}</span>
                                    </td>
                                    <td>
                                        {queueDir === 'queue' && (
                                            <>
                                                <Button size="sm" className="me-2" onClick={() => mergeQueueItem(item)}>Merge</Button>
                                                <Button size="sm" variant="outline-info" className="me-2" onClick={() => navigate(`/dev/fingerprint-workflow?mergeFile=${encodeURIComponent(item.name)}&cloud=${cloudQueue}&fromMerge=1`)}>Workflow</Button>
                                                <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => showRaw(item)}>Raw</Button>
                                                <Button size="sm" variant="outline-secondary" onClick={() => deleteQueueItem(item)}>Delete</Button>
                                            </>
                                        )}
                                        {isArchiveDir && (
                                            <>
                                                <Button size="sm" className="me-2" onClick={() => mergeQueueItem(item)}>Merge</Button>
                                                <Button size="sm" className="me-2" onClick={() => requeueItem(item)}>Requeue</Button>
                                                <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => showRaw(item)}>Raw</Button>
                                                <Button size="sm" variant="outline-secondary" onClick={() => deleteQueueItem(item)}>Delete</Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </Container>

        {/* Raw JSON viewer */}
        <Modal show={Boolean(rawItem)} onHide={() => setRawItem(null)} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{rawItem?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {rawItem?.content
                        ? (() => { try { return JSON.stringify(JSON.parse(rawItem.content), null, 2); } catch { return rawItem.content; } })()
                        : ''}
                </pre>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setRawItem(null)}>Close</Button>
            </Modal.Footer>
        </Modal>
        </>
    );
}

export default MergeQueuePage;
